import React, { memo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home, LayoutDashboard, Package, LogOut, Upload, Book, AlertOctagon,
  ClipboardList, ClipboardCheck, FileText, Calendar, Shield, ChevronDown, ChevronRight,
  ScrollText, Warehouse, Truck, Ship, Tent, Store, TrendingUp, Award, Users, RotateCcw,
  Lasso, Container, UserCog, X, Globe, Lightbulb, MapPin, Footprints, Clock, Map, Factory,
  ShoppingBag, BarChart3, Layers, Zap, Target, Radar, BadgeDollarSign, CalendarClock,
  Calculator, Scissors, Handshake, Binoculars, Tag, FilePlus, ShieldCheck,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { B } from '../utils/constants';
import { useRole } from '../context/RoleContext';

/**
 * The nav, as one structure.
 *
 * A DOOR is a top-level workspace. It keeps the ranch name the team already
 * says out loud and carries a `sign` — the small-caps line underneath that
 * tells a newcomer what is in the pasture. Doors hold a flat `items` list,
 * `clusters` (a captioned group of items), or both – items draw first, then
 * the clusters; a cluster caption is a signpost, not a toggle, so it costs
 * no click and no expand state.
 *
 * Two levels is the floor: door, then page. Where a page genuinely has sibling
 * routes (Margin Reporting, Material Planning, Margin Builder) those still
 * hang off the item as `children` until they become in-page tab bars — the
 * routes exist, so dropping them from the nav would orphan real pages.
 *
 * Item `id` must match a `key` in the `resources` table and `path` must match
 * a <Route> in App.jsx — the deploy checklist checks both.
 */
export const NAV = [
  {
    id: 'round-up', label: 'Supply Round Up', sign: 'Buying · orders · freight', icon: Lasso,
    clusters: [
      {
        label: 'The plan',
        items: [
          { id: 'supply-plan', label: 'Supply Plan', icon: TrendingUp, path: '/supply-chain/supply-plan' },
          { id: 'buy-ready', label: 'Buy Ready', icon: Clock, path: '/supply-chain/buy-ready' },
          { id: 'launch-lineup', label: 'Launch Line Up', icon: Calendar, path: '/supply-chain/launch-lineup' },
          { id: 'markdowns', label: 'Markdowns', icon: Tag, path: '/supply-chain/markdowns', beta: true },
        ],
      },
      {
        label: 'Purchase orders',
        items: [
          { id: 'grit-grind', label: 'Grit and Grind', icon: Package, path: '/supply-chain/grit-grind' },
          { id: 'trail-notes', label: 'Trail Notes', icon: FileText, path: '/supply-chain/trail-notes' },
          { id: 'off-cycle-po', label: 'Off-Cycle POs', icon: FilePlus, path: '/supply-chain/off-cycle-po' },
          { id: 'mx-launch-tracker', label: 'Mexico Production', icon: Factory, path: '/supply-chain/mx-launch-tracker' },
        ],
      },
      {
        // Cargo Signal comes up out of the Sheriff's Office: it's the page you
        // open right after Pipeline, not a piece of system administration.
        label: 'On the trail',
        items: [
          { id: 'pipeline', label: 'Pipeline', icon: Ship, path: '/supply-chain/pipeline' },
          { id: 'cargo-signal', label: 'Cargo Signal', icon: MapPin, path: '/supply-chain/cargo-signal' },
          { id: 'wfx-projection-import', label: 'WFX Projections', icon: Upload, path: '/planning/wfx-import' },
        ],
      },
    ],
  },
  {
    id: 'barn', label: 'The Barn', sign: 'Inventory', icon: Warehouse,
    items: [
      { id: 'the-barn', label: 'Inventory', icon: Container, path: '/supply-chain/the-barn' },
      { id: 'trail-count', label: 'Trail Count', icon: Radar, path: '/supply-chain/trail-count' },
    ],
  },
  {
    // The stores' own door, keyed by store (store_order_schedule, outbound
    // pipeline). Store Inbound left the rail on 2026-10-07 (Nate): the route,
    // resource key and grants stay so the page still answers at its URL.
    id: 'general-store', label: 'General Store', sign: 'Store operations', icon: Store,
    items: [
      { id: 'outbound-pipeline', label: 'Outbound Pipeline', icon: Truck, path: '/supply-chain/outbound-pipeline' },
      { id: 'store-order-schedule', label: 'Store Schedule', icon: CalendarClock, path: '/customer/store-order-schedule' },
    ],
  },
  {
    id: 'howdy', label: 'Howdy, Partner', sign: 'Customer orders', icon: Users,
    clusters: [
      {
        label: 'Out the door',
        items: [
          { id: 'order-recon', label: 'Order Recon', icon: ClipboardCheck, path: '/customer/order-recon' },
          { id: 'customer-journey-operations', label: 'Operator Dashboard', icon: Zap, path: '/customer-journey/operations' },
        ],
      },
      {
        label: 'On the way',
        items: [
          { id: 'boots-on-the-move', label: 'Boots on the Move', icon: Footprints, path: '/customer/boots-on-the-move' },
          { id: 'customer-journey', label: 'Customer Journey', icon: Map, path: '/customer/journey' },
        ],
      },
      {
        // Custom Boots and Embroidery are the same journey — Shopify through
        // Geodis to a door — so they sit together, out of Supply Round Up.
        label: 'Made to order',
        items: [
          { id: 'custom-boots', label: 'Custom Boots', icon: Footprints, path: '/supply-chain/custom-boots' },
          { id: 'embroidery', label: 'Embroidery', icon: Scissors, path: '/supply-chain/embroidery' },
        ],
      },
    ],
  },
  {
    id: 'exchange', label: 'The Exchange', sign: 'Returns & quality', icon: RotateCcw,
    items: [
      { id: 'the-exchange', label: 'Return Analytics', icon: TrendingUp, path: '/supply-chain/the-exchange' },
      { id: 'quality-concerns', label: 'Quality Concerns', icon: AlertOctagon, path: '/supply-chain/quality-concerns' },
    ],
  },
  {
    // Up a level, out from under the QBR. Material Planning joins it: its own
    // subject is material demand by supplier, with a tannery-handoff lens.
    id: 'trading-post', label: 'Trading Post', sign: 'Vendors & materials', icon: Handshake,
    items: [
      { id: 'vendors-lineup', label: 'Vendor Line Up', icon: LayoutDashboard, path: '/vendors/line-up' },
      // Terms, open POs, open bills and the document register in one grade
      // per vendor. Replaces Jay's Monday "Vendor Compliance Tracking" board
      // (seeded 2026-10-07); NetSuite's six compliance fields stay dead.
      { id: 'vendors-health', label: 'Vendor Health', icon: ShieldCheck, path: '/vendors/health' },
      { id: 'vendors-tanneries', label: 'Tanneries', icon: Factory, path: '/vendors/tanneries' },
      {
        id: 'material-planning', label: 'Material Planning', icon: Layers, path: '/workshop/material-planning',
        children: [
          { id: 'material-planning', label: 'Leather', path: '/workshop/material-planning' },
          // Label says "Apparel Fabric" (the page plans fabric only) while the URL stays /apparel — deliberate, don't "fix" the path.
          { id: 'material-planning', label: 'Apparel Fabric', path: '/workshop/material-planning/apparel' },
          { id: 'material-planning', label: 'Accessory Leather & Hardware', path: '/workshop/material-planning/accessories', beta: true },
          // RSL Testing is behind its OWN resource key (Syd / Victoria / Admins),
          // unlike the three siblings which share the parent's — the per-child
          // access check in the render map hides the link for everyone else.
          { id: 'rsl-testing', label: 'RSL Testing', path: '/workshop/material-planning/rsl-testing' },
        ],
      },
      { id: 'vendor-actions', label: 'Vendor Actions', icon: ClipboardList, path: '/vendors/vendor-actions' },
      { id: 'vendor-approvals', label: 'Approvals', icon: ClipboardCheck, path: '/vendors/approvals' },
    ],
  },
  {
    // Cost, price and margin in one door instead of three: the accounting
    // views come up out of Supply Round Up, the WFX margin pages come over
    // from the retired Workshop.
    id: 'ledger', label: 'The Ledger', sign: 'Cost, price & margin', icon: BadgeDollarSign,
    clusters: [
      {
        label: 'Money out',
        items: [
          { id: 'cash-flow', label: 'Cash Flow', icon: BadgeDollarSign, path: '/supply-chain/cash-flow' },
          { id: 'po-price-audit', label: 'PO Price Audit', icon: ClipboardCheck, path: '/supply-chain/po-price-audit' },
        ],
      },
      {
        label: 'Money in',
        items: [
          { id: 'sfs-ledger', label: 'SFS Orders', icon: Store, path: '/supply-chain/sfs-ledger' },
        ],
      },
      {
        label: 'Margin',
        items: [
          {
            id: 'plm-dashboards', label: 'Margin Reporting', icon: BarChart3, path: '/workshop/margin-reporting',
            children: [
              { id: 'plm-dashboards', label: 'Per Season', path: '/workshop/margin-reporting/season-reporting' },
              { id: 'plm-dashboards', label: 'Historical Comparison', path: '/workshop/margin-reporting/season-over-season' },
            ],
          },
          {
            id: 'margin-builder', label: 'Margin Builder', icon: Calculator, path: '/workshop/margin-builder',
            children: [
              { id: 'margin-builder', label: 'Estimator', path: '/workshop/margin-builder' },
              { id: 'margin-builder-assumptions', label: 'Assumptions', path: '/workshop/margin-builder/assumptions' },
            ],
          },
        ],
      },
    ],
  },
  {
    // The wholesale team's own reporting home. "The Mercantile" is the name
    // the app already owns — it labelled the WFX pages until v1.71.0 relabelled
    // them The Workshop, and its /mercantile/* routes redirect away.
    id: 'mercantile', label: 'The Mercantile', sign: 'Wholesale accounts', icon: ShoppingBag,
    items: [
      { id: 'wholesale', label: 'Overview', icon: LayoutDashboard, path: '/wholesale' },
      { id: 'wholesale-sales', label: 'Sales Performance', icon: TrendingUp, path: '/wholesale/sales' },
      { id: 'wholesale-zm10', label: 'ZM10 Pipeline', icon: Package, path: '/wholesale/zm10' },
      { id: 'wholesale-fill-rate', label: 'Trail Boss', icon: Target, path: '/wholesale/fill-rate' },
      { id: 'wholesale-price-check', label: 'Price Check', icon: BadgeDollarSign, path: '/wholesale/price-check' },
    ],
  },

  // ── the utility rail, below the divider ──────────────────────────────────
  {
    // The QBR deck carries its own section tabs, so the nav links the page and
    // stops there instead of listing seven hash anchors four levels deep.
    id: 'lookout', label: 'The Lookout', sign: 'Reviews & briefings', icon: Binoculars, utility: true,
    items: [
      {
        // The deck's individual section pages, restored (they left the rail in
        // v1.291.0), in exec-summary scorecard order (2026-07-23 request).
        // Most are #hash pages of the active quarter's deck; Wholesale is a
        // real route riding in the same list, directly after Logistics,
        // gated by its own 'qbr-wholesale' key. Warehouse Operations (DC / 3PL
        // metrics) and Logistics (carrier metrics) were one "Ops & Logistics"
        // section until the Q3 2026 deck split them (2026-09-11).
        id: 'qbr', label: 'Supply Chain QBR', icon: TrendingUp, path: '/supply-chain/qbr',
        children: [
          { id: 'qbr', label: 'Exec Summary', path: '/supply-chain/qbr#exec' },
          { id: 'qbr', label: 'Logistics', path: '/supply-chain/qbr#log' },
          { id: 'qbr', label: 'Warehouse Operations', path: '/supply-chain/qbr#ops' },
          { id: 'qbr-wholesale', label: 'Wholesale', path: '/supply-chain/qbr/wholesale' },
          { id: 'qbr', label: 'Planning + Allocation', path: '/supply-chain/qbr#plan' },
          { id: 'qbr', label: 'Sourcing', path: '/supply-chain/qbr#src' },
          { id: 'qbr', label: 'Product Development', path: '/supply-chain/qbr#pd' },
          { id: 'qbr', label: 'Quality Control', path: '/supply-chain/qbr#qc' },
        ],
      },
      { id: 'ci-briefing', label: 'Peak & CI Briefings', icon: Truck, path: '/ci-briefing' },
      { id: 'bfcm-plan', label: 'BFCM 2026 Plan & CI', icon: ShoppingBag, path: '/bfcm-plan' },
    ],
  },
  {
    id: 'sheriff', label: "Sheriff's Office", sign: 'Syncs, fixes & access', icon: Shield, utility: true,
    items: [
      { id: 'data-corral', label: 'Data Corral', icon: Upload, path: '/data-corral' },
      { id: 'wild-west', label: 'Wild West', icon: AlertOctagon, path: '/wild-west' },
      { id: 'outpost', label: 'Outpost', icon: Tent, path: '/outpost' },
      { id: 'admin-carriers', label: 'Horse Stable', icon: Truck, path: '/admin/carriers', adminOnly: true },
      { id: 'process-map', label: 'Process Map', icon: Map, path: '/process-map' },
      { id: 'admin-roles', label: 'Roles', icon: Shield, path: '/admin/roles', adminOnly: true },
      { id: 'admin-users', label: 'Users', icon: UserCog, path: '/admin/users', adminOnly: true },
    ],
  },
  {
    // The whole ask-and-ship loop in one place: how it works, what you'd like
    // it to do, and what shipped.
    id: 'haystack', label: 'The Haystack', sign: 'Guide, ideas & releases', icon: Layers, utility: true,
    items: [
      { id: 'user-guide', label: 'User Guide', icon: Book, path: '/user-guide', alwaysOn: true },
      { id: 'feature-request', label: 'Your Ideas', icon: Lightbulb, path: '/feature-request' },
      { id: 'admin-feature-requests', label: 'Idea Inbox', icon: ClipboardList, path: '/admin/feature-requests', adminOnly: true },
      { id: 'backlog', label: 'Backlog', icon: ClipboardList, path: '/backlog' },
      { id: 'changelog', label: 'Changelog', icon: ScrollText, path: '/changelog' },
    ],
    clusters: [
      {
        // Staging holds pages that are built and gated but not yet signed off
        // for their real door. They keep their route, resource key and Beta
        // badge – only the shelf moves. Projection vs POs came out of Supply
        // Round Up › The plan on 2026-09-23 while the report is validated.
        // Vendor Scorecard came out of Trading Post on 2026-10-06 (Fletcher):
        // not ready to sit in the vendor rail yet; route, key, grants unchanged.
        // The Pony Express (SFS items + on-hand by bucket) landed here 2026-10-27 pending Kari's sign-off; destination The Barn.
        label: 'Staging',
        items: [
          { id: 'projection-vs-po', label: 'Projection vs POs', icon: Target, path: '/supply-chain/projection-vs-po', beta: true },
          { id: 'vendors-scorecard', label: 'Vendor Scorecard', icon: Award, path: '/vendors/scorecard', beta: true },
          { id: 'pony-express', label: 'The Pony Express', icon: Truck, path: '/supply-chain/pony-express', beta: true },
        ],
      },
    ],
  },
];

// Every item in a door, flattened — used for the door's active/expanded state.
const doorItems = (door) => (door.items || []).concat(...(door.clusters || []).map((c) => c.items));
const doorPaths = (door) =>
  doorItems(door).flatMap((i) => [i.path, ...(i.children || []).map((c) => c.path)]);

function Sidebar({ user, onLogout, onClose }) {
  const location = useLocation();
  const { hasAccess, isAdmin } = useRole();
  const { t, i18n } = useTranslation();

  // Language toggle handler - handle language variants like en-US
  const currentLang = i18n.language?.startsWith('es') ? 'es' : 'en';
  const toggleLanguage = () => i18n.changeLanguage(currentLang === 'en' ? 'es' : 'en');

  // A child path may carry a '#section' (the QBR deck's pages); everything
  // before the '#' is what the router's pathname can match against.
  const pathOf = (p) => p.split('#')[0];
  const onPath = (p) => !!p && location.pathname.startsWith(pathOf(p));
  const isOnDoor = (door) => doorPaths(door).some(onPath);

  // Doors start closed so the rail reads as a short table of contents; each
  // one opens by hand. Items that carry children also start closed, unless
  // you're already inside one.
  const [expanded, setExpanded] = useState(() => {
    const state = {};
    NAV.forEach((door) => {
      state[door.id] = false;
      doorItems(door).forEach((item) => {
        if (item.children) state[item.id] = item.children.some((c) => onPath(c.path));
      });
    });
    return state;
  });
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const canOpen = (item) => {
    if (item.alwaysOn) return true;
    if (item.adminOnly) return isAdmin;
    if (item.children) return item.children.some((c) => hasAccess('page', c.id));
    return hasAccess('page', item.id);
  };

  const isActive = (path) => (path === '/' ? location.pathname === '/' : onPath(path));

  const rowStyle = (active) => ({
    backgroundColor: active ? B.olive : 'transparent',
    color: active ? '#fff' : B.sand,
    fontFamily: 'Gill Sans, sans-serif',
  });
  const beta = (item) =>
    item.beta ? (
      <span
        className="text-[8px] font-bold px-1 py-0.5 rounded"
        style={{ backgroundColor: B.rust, color: '#fff', letterSpacing: 0.5 }}
      >
        BETA
      </span>
    ) : null;

  // ── one item row: a link, a locked stub, or a parent with children ────────
  const renderItem = (item) => {
    const Icon = item.icon;

    if (!canOpen(item)) {
      return (
        <div
          key={item.id + item.path}
          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 cursor-not-allowed"
          style={{ ...rowStyle(false), color: B.stone, opacity: 0.5 }}
        >
          <Icon className="w-3.5 h-3.5" />
          <span>{item.label}</span>
          {beta(item)}
        </div>
      );
    }

    if (item.children) {
      const open = expanded[item.id];
      const active = item.children.some((c) => location.pathname === c.path);
      return (
        <div key={item.id}>
          <button
            onClick={() => toggle(item.id)}
            className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
            style={{
              ...rowStyle(false),
              backgroundColor: active ? B.olive + 'cc' : 'transparent',
              color: active ? '#fff' : B.sand,
            }}
          >
            <div className="flex items-center gap-2" style={{ color: active ? '#fff' : B.sand }}>
              <Icon className="w-3.5 h-3.5" />
              <span>{item.label}</span>
              {beta(item)}
            </div>
            {open ? (
              <ChevronDown className="w-3 h-3 opacity-60" />
            ) : (
              <ChevronRight className="w-3 h-3 opacity-60" />
            )}
          </button>
          {open && (
            <div className="ml-4 mb-1">
              {item.children.map((child) => {
                if (!hasAccess('page', child.id)) return null;
                // A '#section' child is one page of a deck: it lights up only
                // on its exact hash. For plain children, an exact path match
                // always wins over the prefix fallback — one child's path can
                // be a prefix of another's (Leather lights up on Apparel).
                const exact = item.children.find((c) => c.path === location.pathname);
                const childActive = child.path.includes('#')
                  ? location.pathname + location.hash === child.path
                  : exact ? exact.path === child.path : onPath(child.path + '/');
                return (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={onClose}
                    className="w-full flex items-center gap-2 px-3 py-1 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
                    style={{ ...rowStyle(childActive), opacity: child.underConstruction ? 0.6 : 1 }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: childActive ? '#fff' : B.stone + '80' }}
                    />
                    <span>{child.label}</span>
                    {beta(child)}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path}
        onClick={onClose}
        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-0.5 transition-all hover:opacity-80"
        style={{ ...rowStyle(isActive(item.path)), opacity: item.underConstruction ? 0.6 : 1 }}
      >
        <Icon className="w-3.5 h-3.5" />
        <span>{item.label}</span>
        {beta(item)}
      </Link>
    );
  };

  // ── one door: the ranch name, its sign, and what's behind it ─────────────
  const renderDoor = (door) => {
    const Icon = door.icon;
    const active = isOnDoor(door);
    const open = expanded[door.id];
    return (
      <div key={door.id}>
        <button
          onClick={() => toggle(door.id)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={rowStyle(active)}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="w-4 h-4 flex-shrink-0" />
            <span className="flex flex-col items-start leading-tight text-left">
              <span>{door.label}</span>
              <span
                className="text-[8px] font-medium uppercase whitespace-nowrap"
                style={{ color: active ? B.sand : B.sage, letterSpacing: '0.07em' }}
              >
                {door.sign}
              </span>
            </span>
          </div>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {open && (
          <div className="ml-3">
            {(door.items || []).map(renderItem)}
            {(door.clusters || []).map((cluster) => (
              <div key={cluster.label}>
                <div
                  className="px-3 pt-2 pb-1 text-[9px] font-semibold uppercase"
                  style={{ color: B.sage, letterSpacing: '0.13em' }}
                >
                  {cluster.label}
                </div>
                {cluster.items.map(renderItem)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-56 h-full p-5 flex flex-col" style={{ backgroundColor: B.forest }}>
      {/* Logo with Close Button for Mobile/Tablet */}
      <div className="mb-6 flex items-center justify-between">
        <Link to="/" className="block" onClick={onClose}>
          <h1
            className="text-lg font-semibold tracking-widest text-white"
            style={{ fontFamily: 'Gill Sans, sans-serif' }}
          >
            {t('nav.tecovas')}
          </h1>
          <p className="text-xs" style={{ color: B.sage }}>{t('nav.supplyChain')}</p>
        </Link>
        {onClose && (
          <button onClick={onClose} className="xl:hidden p-2 rounded-lg hover:bg-white/10 transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        )}
      </div>

      {/* Navigation — scrolls on its own when the open rail outgrows the
          viewport (the mobile drawer is fixed-height, so without this the
          lower doors would be unreachable there) */}
      <nav className="flex-1 overflow-y-auto min-h-0 pr-1">
        <Link
          to="/"
          onClick={onClose}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-1 transition-all hover:opacity-80"
          style={{ ...rowStyle(location.pathname === '/'), fontWeight: 600 }}
        >
          <Home className="w-4 h-4" />
          <span>{t('nav.homestead')}</span>
        </Link>

        <div className="h-px my-2 mx-2" style={{ backgroundColor: B.olive }} />

        {NAV.filter((d) => !d.utility).map(renderDoor)}

        <div className="h-px my-3 mx-2" style={{ backgroundColor: B.olive }} />

        {NAV.filter((d) => d.utility).map(renderDoor)}
      </nav>

      {/* Language Switcher */}
      <div className="mb-3 mt-3">
        <button
          onClick={toggleLanguage}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
          style={rowStyle(false)}
        >
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span>{t('language.selectLanguage')}</span>
          </div>
          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: B.olive }}>
            {currentLang === 'en' ? 'EN' : 'ES'}
          </span>
        </button>
      </div>

      {/* User info and logout */}
      <div className="border-t pt-4" style={{ borderColor: B.olive }}>
        <p className="text-xs truncate mb-2" style={{ color: B.sand }}>{user?.email}</p>
        <button
          onClick={onLogout}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs w-full hover:opacity-80 transition"
          style={{ backgroundColor: B.rust, color: '#fff' }}
        >
          <LogOut className="w-4 h-4" /> {t('nav.logout')}
        </button>
      </div>
    </div>
  );
}

export default memo(Sidebar);
